import React, { useState } from 'react';
import type { Avocat } from '../types';
import { Button, Card, Input } from './UI';

interface ConventionEditorProps {
  data: Avocat;
  onDataChange: (data: Avocat) => void;
}

export function ConventionEditor({ data, onDataChange }: ConventionEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Avocat>(data);

  const handleSave = () => {
    if (!formData.nom || !formData.barreau || !formData.email) {
      alert('Veuillez remplir les champs requis');
      return;
    }
    onDataChange(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(data);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>Éditer mes informations</h1>

        <Card style={{ maxWidth: 800 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Nom Complet *
                </label>
                <Input
                  value={formData.nom || ''}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Maître Jean Dupont"
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Barreau *
                </label>
                <Input
                  value={formData.barreau || ''}
                  onChange={(e) => setFormData({ ...formData, barreau: e.target.value })}
                  placeholder="Ex: Barreau de Paris"
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Adresse
              </label>
              <Input
                value={formData.adresse || ''}
                onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                placeholder="Ex: 123 rue de la Paix, 75000 Paris"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Téléphone
                </label>
                <Input
                  value={formData.tel || ''}
                  onChange={(e) => setFormData({ ...formData, tel: e.target.value })}
                  placeholder="Ex: +33 1 23 45 67 89"
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Ex: contact@avocat.fr"
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                SIRET
              </label>
              <Input
                value={formData.siret || ''}
                onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                placeholder="Ex: 12345678901234"
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} variant="secondary">
                Annuler
              </Button>
              <Button onClick={handleSave}>
                Enregistrer
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Mes informations</h1>
        <Button onClick={() => setIsEditing(true)}>✎ Éditer</Button>
      </div>

      <Card style={{ maxWidth: 800 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Header */}
          <div style={{ borderBottom: '1px solid var(--color-border-secondary)', paddingBottom: '1.5rem' }}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: 20, fontWeight: 600 }}>
              {data.nom || 'Non renseigné'}
            </h2>
            <p style={{ margin: '0 0 0.25rem', fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {data.barreau || 'Barreau non renseigné'}
            </p>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {data.email || 'Email non renseigné'}
            </p>
          </div>

          {/* Details */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            <div>
              <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 500, textTransform: 'uppercase' }}>
                Adresse
              </p>
              <p style={{ margin: 0, fontSize: 13 }}>
                {data.adresse || 'Non renseignée'}
              </p>
            </div>

            <div>
              <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 500, textTransform: 'uppercase' }}>
                Téléphone
              </p>
              <p style={{ margin: 0, fontSize: 13 }}>
                {data.tel || 'Non renseigné'}
              </p>
            </div>

            <div>
              <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 500, textTransform: 'uppercase' }}>
                SIRET
              </p>
              <p style={{ margin: 0, fontSize: 13 }}>
                {data.siret || 'Non renseigné'}
              </p>
            </div>
          </div>

          {/* Legal info */}
          <div style={{ borderTop: '1px solid var(--color-border-secondary)', paddingTop: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: 14, fontWeight: 600 }}>
              Conditions générales
            </h3>
            <div style={{ backgroundColor: 'var(--color-background-secondary)', padding: '1rem', borderRadius: 'var(--border-radius-md)', fontSize: 12, lineHeight: 1.6, color: 'var(--color-text-secondary)' }}>
              <p style={{ margin: '0 0 0.75rem' }}>
                <strong>Délais de paiement :</strong> Net 30 jours à réception de facture
              </p>
              <p style={{ margin: '0 0 0.75rem' }}>
                <strong>Modalités de facturation :</strong>
              </p>
              <ul style={{ margin: '0 0 0.75rem 1.5rem', paddingLeft: 0 }}>
                <li>Mode forfait: montant fixe défini à la convention</li>
                <li>Mode horaire: taux à l'heure multiplié par le temps engagé</li>
              </ul>
              <p style={{ margin: '0 0 0.75rem' }}>
                <strong>Frais divers :</strong> Débours et frais annexes facturés en sus
              </p>
              <p style={{ margin: 0 }}>
                <strong>Taux horaire :</strong> Défini selon le type de dossier (voir dossiers)
              </p>
            </div>
          </div>

          {/* Professional standards */}
          <div>
            <h3 style={{ margin: '0 0 1rem', fontSize: 14, fontWeight: 600 }}>
              Obligations professionnelles
            </h3>
            <div style={{ backgroundColor: 'var(--color-background-secondary)', padding: '1rem', borderRadius: 'var(--border-radius-md)', fontSize: 12, lineHeight: 1.6, color: 'var(--color-text-secondary)' }}>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li>Respect du secret professionnel</li>
                <li>Indépendance et impartialité</li>
                <li>Respect des délais et procédures</li>
                <li>Conseil loyal et désintéressé</li>
                <li>Responsabilité civile professionnelle obligatoire</li>
                <li>Conformité RGPD et protection des données</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
